'use client';
import { History as HistoryIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { GridActionsCellItem, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { GridColDef, getGridDateOperators, getGridNumericOperators } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { Dispatch, SetStateAction } from 'react';

import type { SetShowDialog } from '../../types';

import { formatCurrency, isAdminDashboard, isOwnerPortal } from '../../utils';
import { AuditHistoryModal } from '../audit';
import { SyncButton } from '../sync';
import { ParkerDetailModal } from './parkerDetail';

export const defaultOrder = {
	model: 'PARKER',
	field: 'created_at',
	direction: 'DESC',
};
export const defaultOrderForV2 = {
	field: 'createdAt',
	sort: 'desc',
};

export function capitalizeFirstLetter(str: string) {
	switch (str) {
		case null: {
			return '';
		}
		case 'termination_pending': {
			const splitStr = str.split('_');
			const string1 = splitStr[0].charAt(0).toUpperCase() + splitStr[0].slice(1);
			const string2 = splitStr[1].charAt(0).toUpperCase() + splitStr[1].slice(1);
			const final = `${string1} ${string2}`;
			return final;
		}
		default:
			return str.charAt(0).toUpperCase() + str.slice(1);
	}
}

export const onFilterModelChangeForCache = (
	filterModel: GridFilterModel,
	setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>,
	setCacheFilters: Dispatch<SetStateAction<any>>,
) => {
	setPaginationModel((prev) => ({ ...prev, page: 0 }));

	const { items: filterItems } = filterModel;

	if (!filterItems.length) {
		setCacheFilters([]);
		return;
	}

	const queryFilters: any = filterItems
		.map((filter) => {
			const { field, operator, value: rawValue } = filter;

			if (!rawValue && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];
			let newValue = rawValue;
			if (field === 'gross' || field === 'net') {
				newValue = Number(rawValue) * 100;
			}
			return {
				propertyName: field,
				op: operator,
				value: newValue,
			};
		})
		.flat();

	if (queryFilters.length) setCacheFilters(queryFilters);
};

export function onSortModelChangeForCache(
	sortModel: GridSortModel,
	setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>,
	setCacheSortOrder: Dispatch<SetStateAction<any>>,
) {
	setPaginationModel((prev) => ({ ...prev, page: 0 }));
	if (!sortModel.length) {
		return setCacheSortOrder(defaultOrderForV2);
	}

	const { field, sort } = sortModel[0];
	if (!sort) return null;
	return setCacheSortOrder({
		field,
		sort,
	});
}

export function getParkersTableColumns({
	parkerDetailModal,
	setShowDialog,
	tenantSubscriptionId,
	getRateForm,
	syncingRowId,
	handleSyncInCache,
	refetchParkersFromCache,
	theme,
	hasComponentAccess,
}: {
	parkerDetailModal: ((props: any) => React.ReactElement) | null;
	setShowDialog: SetShowDialog;
	tenantSubscriptionId: string;
	getRateForm: (rateId: string) => Promise<React.ReactElement>;
	syncingRowId: string;
	handleSyncInCache: (parkerId: string) => Promise<void>;
	refetchParkersFromCache: () => void;
	theme: any;
	hasComponentAccess: (componentName: string) => boolean;
}): GridColDef[] {
	const style = {
		background: 'none',
		border: 0,
		color: 'blue',
		fontSize: 'inherit',
		cursor: 'pointer',
	};

	const numericOperators = getGridNumericOperators();
	const dateOperators = getGridDateOperators();

	const columnDefs: GridColDef[] = [
		!isOwnerPortal && {
			field: 'actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<>
					<SyncButton
						syncingRowId={syncingRowId}
						id={params.row.id}
						isUpdating={params.row.isUpdating}
						toolTipInfo='Parker is being updated currently'
						onClick={() => handleSyncInCache(params.row.id)}
						disabled={!!syncingRowId && syncingRowId !== params.row.id}
						cacheType='parker'
					/>
					{hasComponentAccess('AuditHistoryTable') && (
						<GridActionsCellItem
							key='history'
							icon={
								<Tooltip title='View Audit History'>
									<HistoryIcon />
								</Tooltip>
							}
							label='History'
							onClick={() =>
								setShowDialog(
									<AuditHistoryModal
										title='Parker Audit History'
										resourceId={params.row.id}
										resourceType={ResourceTypes.PARKER}
										timezone={params.row.timezone}
										onClose={() => setShowDialog(null)}
									/>,
								)
							}
						/>
					)}
				</>
			),
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						if (isOwnerPortal) {
							setShowDialog(
								<ParkerDetailModal
									theme={theme}
									handleClose={() => {
										refetchParkersFromCache();
										setShowDialog(null);
									}}
									setShowSnackbar={null}
									parkerId={params.row.id}
									parkerFirstName={params.row.firstName}
									parkerLastName={params.row.lastName}
									parkerAccountId={params.row.accountId}
									parkerSubscriptionName={params.row.subscriptionName}
									facilityId={params.row.facilityId}
									subscriptionId={params.row.subscriptionId}
									parkerFacilityName={params.row.facilityName}
									parkerFacilityNumber={params.row.facilityNumber}
									refetchParkersFromCache={refetchParkersFromCache}
								/>,
							);
							return;
						}
						if (!parkerDetailModal) return;
						setShowDialog(
							parkerDetailModal({
								parkerId: params.row.id,
								parkerFirstName: params.row.firstName,
								parkerLastName: params.row.lastName,
								parkerAccountId: params.row.accountId,
								parkerSubscriptionName: params.row.subscriptionName,
								facilityId: params.row.facilityId,
								subscriptionId: params.row.subscriptionId,
								parkerFacilityName: params.row.facilityName,
								parkerFacilityNumber: params.row.facilityNumber,
								parkerSequenceNumber: params.row.sequenceNumber,
								handleClose: () => {
									refetchParkersFromCache();
									setShowDialog(null);
								},
								refetchParkersFromCache,
							}),
						);
					}}
					data-cy='last-name-link'
				>
					{params.row.lastName || 'Change Me'}
				</a>
			),
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						if (isOwnerPortal) {
							setShowDialog(
								<ParkerDetailModal
									theme={theme}
									handleClose={() => {
										refetchParkersFromCache();
										setShowDialog(null);
									}}
									setShowSnackbar={null}
									parkerId={params.row.id}
									parkerFirstName={params.row.firstName}
									parkerLastName={params.row.lastName}
									parkerAccountId={params.row.accountId}
									parkerSubscriptionName={params.row.subscriptionName}
									facilityId={params.row.facilityId}
									subscriptionId={params.row.subscriptionId}
									parkerFacilityName={params.row.facilityName}
									parkerFacilityNumber={params.row.facilityNumber}
									refetchParkersFromCache={refetchParkersFromCache}
								/>,
							);
							return;
						}
						if (!parkerDetailModal) return;
						setShowDialog(
							parkerDetailModal({
								parkerId: params.row.id,
								parkerFirstName: params.row.firstName,
								parkerLastName: params.row.lastName,
								parkerAccountId: params.row.accountId,
								parkerSubscriptionName: params.row.subscriptionName,
								facilityId: params.row.facilityId,
								subscriptionId: params.row.subscriptionId,
								parkerFacilityName: params.row.facilityName,
								parkerFacilityNumber: params.row.facilityNumber,
								handleClose: () => {
									refetchParkersFromCache();
									setShowDialog(null);
								},
								refetchParkersFromCache,
							}),
						);
					}}
					data-cy='first-name-link'
				>
					{params.row.firstName || 'Change Me'}
				</a>
			),
		},
		{
			field: 'suspensionStatus',
			headerName: 'Suspension Status',
			width: 200,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'Suspension_Pending', label: 'Suspension Pending' },
				{ value: 'Suspended', label: 'Suspended' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Not_Invoiced', label: 'Not Invoiced' },
				{ value: 'Active', label: 'Active' },
				{ value: 'Terminated', label: 'Terminated' },
			],
		},
		{
			field: 'sublot',
			headerName: 'Sublot Name',
			renderCell: (params) => params.row.sublot?.name || params.row.sublot || null,
		},
		{
			field: 'terminationStatus',
			headerName: 'Termination Status',
			width: 200,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'Termination_Pending', label: 'Termination Pending' },
				{ value: 'Terminated', label: 'Terminated' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Not_Invoiced', label: 'Not Invoiced' },
				{ value: 'Active', label: 'Active' },
			],
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
		},
		{
			field: 'accountId',
			headerName: 'Account ID',
			filterOperators: numericOperators,
			renderCell: (params) => `AC-${params.row.accountId}`,
		},
		{
			field: 'readableId',
			headerName: 'Sub. ID',
			filterOperators: numericOperators,
		},
		{
			field: 'payorType',
			headerName: 'Sub. Type',
		},
		{
			field: 'parkerTypeName',
			headerName: 'Parker Type',
		},
		{
			field: 'residentNonResident',
			headerName: 'R/NR',
			width: 70,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'R', label: 'R' },
				{ value: 'NR', label: 'NR' },
			],
		},
		{
			field: 'residentUnit',
			headerName: 'Resident Unit',
			width: 70,
		},
		{
			field: 'tenantNonTenant',
			headerName: 'T/NT',
			width: 70,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'T', label: 'T' },
				{ value: 'NT', label: 'NT' },
			],
		},
		{
			field: 'tenantName',
			headerName: 'Tenant Name',
			width: 150,
		},
		{
			field: 'rateName',
			headerName: 'Rate Name',
			...(!tenantSubscriptionId &&
				!isOwnerPortal &&
				(!isAdminDashboard || hasComponentAccess('SubscriptionInfoParkersPageRateLink')) && {
					renderCell: (params) => (
						<a
							style={style}
							onClick={async () => {
								const { rateId } = params.row;
								if (rateId) {
									return setShowDialog(await getRateForm(rateId));
								}
								return null;
							}}
							data-cy='rate-name-link'
						>
							{params.row.rateName}
						</a>
					),
				}),
		},
		{
			field: 'discountType',
			headerName: 'Child Discount Type',
			type: 'singleSelect',
			valueOptions: [
				{ value: 'DOLLAR', label: 'Dollar' },
				{ value: 'PERCENT', label: 'Percent' },
				{ value: 'FEE', label: 'Fee' },
			],
		},
		{
			field: 'discountAmount',
			headerName: 'Child Discount Amount',
			filterable: false,
			renderCell: (params) => {
				if (params.row.discountType === 'PERCENT') {
					return `${params.row.discountAmount / 10000}%`;
				}
				if (params.row.discountType === 'DOLLAR') {
					return formatCurrency(params.row.discountAmount / 100);
				}
				return null;
			},
		},
		{
			field: 'net',
			headerName: 'Current Monthly Net Rate',
			filterOperators: numericOperators,
			renderCell: (params) => formatCurrency(params.row.net / 100),
		},
		{
			field: 'gross',
			headerName: 'Current Monthly Gross Rate',
			filterOperators: numericOperators,
			renderCell: (params) => formatCurrency(params.row.gross / 100),
		},
		{
			field: 'passType',
			headerName: 'Pass Type',
		},
		{
			field: 'location',
			headerName: 'Location',
		},
		{
			field: 'group',
			headerName: 'Group',
		},
		{
			field: 'accessHours',
			headerName: 'Access Hours',
			type: 'singleSelect',
			valueOptions: [
				{ value: 'Twenty_Four_Seven_Access', label: '24/7 Access' },
				{ value: 'Nights_and_Weekends', label: 'Nights and Weekends' },
				{ value: 'Monday_through_Friday_Weekday_Daytime', label: 'M-F Weekday Daytime' },
			],
		},
		{
			field: 'reservedSpace',
			headerName: 'Reserved Space',
		},
		{
			field: 'email',
			headerName: 'Email',
		},
		{
			field: 'phone',
			headerName: 'Phone',
		},
		{
			field: 'address',
			headerName: 'Address',
		},
		{
			field: 'city',
			headerName: 'City',
		},
		{
			field: 'state',
			headerName: 'State',
		},
		{
			field: 'zip',
			headerName: 'Zip',
		},
		{
			field: 'parkerStartDate',
			headerName: 'Parker Start Date',
			filterOperators: dateOperators,
			renderCell: (params) =>
				params.row.parkerStartDate ? dayjs.utc(params.row.parkerStartDate).format('YYYY-MM-DD') : null,
		},
		{
			field: 'parkerTerminationDate',
			headerName: 'Termination Date',
			filterOperators: dateOperators,
			renderCell: (params) =>
				params.row.parkerTerminationDate
					? dayjs.utc(params.row.parkerTerminationDate).format('YYYY-MM-DD')
					: null,
		},
	].filter(Boolean);

	if (isOwnerPortal) {
		columnDefs.splice(4, 0, {
			field: 'facilityName',
			headerName: 'Facility Name',
			groupable: false,
		});
		columnDefs.splice(5, 0, {
			field: 'facilityNumber',
			headerName: 'Facility Number',
			filterOperators: numericOperators,
			groupable: false,
		});
	}

	return columnDefs.map((colDef) => ({
		...colDef,
		groupable: false,
	}));
}
